// src/pages/subscription/SubscriptionHistoryPage.tsx

import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useSubscription, useI18n } from '@react/hooks';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Alert,
  Badge,
  ErrorAlert,
} from '@react/components';

function formatLabel(value: string, prefix: string): string {
  return value.replace(prefix, '').split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '--';
  try { return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return String(dateStr); }
}

function formatMoney(amount: number | null | undefined): string {
  if (amount == null) return '--';
  return (amount / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

type ChangeTypeVariant = 'success' | 'warning' | 'error' | 'primary' | 'secondary';

function getChangeTypeVariant(changeType: string): ChangeTypeVariant {
  const type = changeType.toUpperCase();
  if (type.includes('UPGRADE')) return 'success';
  if (type.includes('DOWNGRADE')) return 'warning';
  if (type.includes('CANCEL')) return 'error';
  if (type.includes('REACTIVATE')) return 'primary';
  if (type.includes('PAUSE')) return 'warning';
  if (type.includes('RESUME')) return 'success';
  return 'secondary';
}

interface TimelineEvent {
  id: string;
  type: string;
  label: string;
  description?: string;
  from_plan?: string;
  to_plan?: string;
  reason?: string;
  price_difference?: number;
  effective_at?: string;
  created_at?: string;
}

export const SubscriptionHistoryPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useSubscription();
  const { t } = useI18n();
  const item = store.currentItem;

  useEffect(() => {
    if (id) {
      store.GetSubscription(id);
    }
    return () => {
      store.clearCurrent();
    };
  }, [id]);

  if (store.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (store.error) {
    return (
      <>
        <ErrorAlert error={store.error} />
        <Link to="/subscriptions" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </>
    );
  }

  if (!item) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link to="/subscriptions" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </Alert>
    );
  }

  // Build timeline events from subscription lifecycle data
  const sub = item as any;
  const events: TimelineEvent[] = [];

  if (sub.created_at) {
    events.push({
      id: 'created',
      type: 'CREATED',
      label: t('subscription.created'),
      description: t('subscription.subscriptionCreated'),
      created_at: sub.created_at,
    });
  }

  if (sub.started_at) {
    events.push({
      id: 'started',
      type: 'ACTIVATED',
      label: t('subscription.activated'),
      description: sub.plan?.name
        ? `${t('subscription.plan')}: ${sub.plan.name}`
        : undefined,
      created_at: sub.started_at,
    });
  }

  if (sub.trial_start) {
    events.push({
      id: 'trial_start',
      type: 'TRIAL_START',
      label: t('subscription.trialStarted'),
      description: sub.trial_end
        ? `${t('subscription.trialEnds')}: ${formatDate(sub.trial_end)}`
        : undefined,
      created_at: sub.trial_start,
    });
  }

  if (sub.trial_end) {
    events.push({
      id: 'trial_end',
      type: 'TRIAL_END',
      label: t('subscription.trialEnded'),
      created_at: sub.trial_end,
    });
  }

  if (sub.cancelled_at) {
    events.push({
      id: 'cancelled',
      type: 'CANCEL',
      label: t('subscription.cancelled'),
      reason: sub.cancellation_reason
        ? formatLabel(sub.cancellation_reason, 'CANCELLATION_REASON_')
        : undefined,
      description: sub.cancellation_comment || undefined,
      created_at: sub.cancelled_at,
    });
  }

  if (sub.ends_at) {
    events.push({
      id: 'ends',
      type: 'SCHEDULED_END',
      label: t('subscription.scheduledEnd'),
      created_at: sub.ends_at,
    });
  }

  // Include changes if available
  if (sub.changes && Array.isArray(sub.changes)) {
    sub.changes.forEach((change: any, idx: number) => {
      events.push({
        id: change.entity?.id || change.id || `change-${idx}`,
        type: change.change_type || 'CHANGE',
        label: change.change_type
          ? formatLabel(change.change_type, 'SUBSCRIPTION_CHANGE_TYPE_')
          : t('subscription.planChange'),
        from_plan: change.from_plan?.name ?? change.from_plan_id,
        to_plan: change.to_plan?.name ?? change.to_plan_id,
        reason: change.reason,
        price_difference: change.price_difference,
        effective_at: change.effective_at,
        created_at: change.created_at,
      });
    });
  }

  // Sort events by date descending (newest first)
  events.sort((a, b) => {
    const dateA = a.effective_at || a.created_at || '';
    const dateB = b.effective_at || b.created_at || '';
    return dateB.localeCompare(dateA);
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <nav className="text-sm mb-2">
          <Link to="/subscriptions" className="text-muted-foreground hover:text-foreground">
            {t('subscription.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to={`/subscriptions/${id}`} className="text-muted-foreground hover:text-foreground">
            {id}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('subscription.history')}</span>
        </nav>
        <h1 className="text-2xl font-bold text-foreground">{t('subscription.history')}</h1>
      </div>

      {/* Current Status */}
      <Card>
        <CardHeader>
          <CardTitle>{t('subscription.currentStatus')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('subscription.status')}</p>
              <p className="text-sm text-foreground mt-1">
                {sub.status ? (
                  <Badge variant="primary" size="sm">
                    {formatLabel(sub.status, 'SUBSCRIPTION_STATUS_')}
                  </Badge>
                ) : '--'}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('subscription.plan')}</p>
              <p className="text-sm text-foreground mt-1">
                {String(sub.plan?.name ?? sub.plan_id ?? '--')}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('subscription.startedAt')}</p>
              <p className="text-sm text-foreground mt-1">{formatDate(sub.started_at)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('subscription.currentPeriod')}</p>
              <p className="text-sm text-foreground mt-1">
                {formatDate(sub.current_period_start)} - {formatDate(sub.current_period_end)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card>
        <CardHeader>
          <CardTitle>{t('subscription.timeline')}</CardTitle>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('subscription.noHistoryEvents')}</p>
          ) : (
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border" />

              <div className="space-y-6">
                {events.map((event) => {
                  const variant = getChangeTypeVariant(event.type);
                  const eventDate = event.effective_at || event.created_at;

                  return (
                    <div key={event.id} className="relative flex gap-4 pl-10">
                      {/* Timeline dot */}
                      <div className="absolute left-2.5 top-1.5 h-3 w-3 rounded-full border-2 border-background bg-primary ring-2 ring-border" />

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant={variant} size="sm">
                              {event.label}
                            </Badge>
                            {event.from_plan && event.to_plan && (
                              <span className="text-sm text-muted-foreground">
                                {event.from_plan}
                                <svg className="inline-block mx-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                </svg>
                                {event.to_plan}
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatDate(eventDate)}
                          </span>
                        </div>

                        {event.description && (
                          <p className="text-sm text-muted-foreground mt-1">{event.description}</p>
                        )}

                        {event.reason && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {t('subscription.reason')}: {event.reason}
                          </p>
                        )}

                        {event.price_difference != null && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {t('subscription.priceDifference')}: {formatMoney(event.price_difference)}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
});

export default SubscriptionHistoryPage;
